import * as React from 'react';
import { cn } from '@/util/cn';
import { useSanitizedHtmlProps } from '@/util/sanitized';

// Renders API-supplied prose with the shared prose typography. Inputs may
// contain markdown-style structure (h2 / p / a / ul / ol / li) — the
// API converts to HTML server-side before delivery. This component
// sanitizes the HTML (XSS guard via useSanitizedHtmlProps) and styles
// the resulting elements with our Inter/Poppins ladder.
//
// One required prop: `text`. No tab orchestration, no collapse, no
// resize observer, no title prop. If a heading is needed above the
// content, render an <h2> yourself at the call-site so the caller
// owns the document outline.
//
// Used by TrailMap (single terrain description block) and any future
// surface that needs to render a chunk of CMS-style content.

const proseClass = cn(
  'text-sm leading-relaxed text-foreground',
  '[&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:font-heading [&_h2]:text-lg [&_h2]:text-secondary',
  '[&_h3]:mb-2 [&_h3]:mt-3 [&_h3]:font-heading [&_h3]:text-base [&_h3]:text-secondary',
  '[&_p]:mb-3 [&_p:last-child]:mb-0',
  '[&_a]:text-primary [&_a]:underline hover:[&_a]:text-primary/80',
  '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:mb-1',
  '[&_strong]:font-semibold [&_strong]:text-foreground',
  '[&_em]:italic',
);

export type MarkdownContentProps = {
  /** Sanitized prose. Pass the HTML string from the API. */
  text: string | undefined;
  className?: string;
};

export const MarkdownContent = ({ text, className }: MarkdownContentProps) => {
  const htmlProps = useSanitizedHtmlProps(text || '');
  if (!text) return null;
  return <div {...htmlProps} className={cn(proseClass, className)} />;
};
